import { useEffect, useRef, useState } from "react"
import { Check } from "lucide-react"

interface ProcessingCoreProps {
  /** Orb illustration shown with the animated percentage. */
  orb: string
  /** Optional ring overlaid on the orb (e.g. the compatibility processing ring). */
  ring?: string
  title: string
  /** One label + progress bar per entry; bars fill sequentially. */
  checklist: string[]
  /** Called once the bars are full and the completed state has been held. */
  onComplete: () => void
  /** Total ramp time 0→100%. */
  durationMs?: number
  /** How long to hold the completed (all-checked) state before onComplete. */
  holdMs?: number
}

/**
 * Shared processing animation: an orb with an animated percentage and a set of
 * sequential progress bars, each showing a checkmark once it fills. Used by both
 * the palmistry/simplified Analysis screen and the Compatibility processing
 * screen — they differ only in the orb asset and what happens on completion.
 */
export function ProcessingCore({
  orb,
  ring,
  title,
  checklist,
  onComplete,
  durationMs = 5000,
  holdMs = 1400,
}: ProcessingCoreProps) {
  const [percent, setPercent] = useState(0)
  const [doneBars, setDoneBars] = useState<number[]>([])
  const n = checklist.length || 1

  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  })

  useEffect(() => {
    const step = 100 / (durationMs / 50)
    const id = setInterval(() => setPercent((p) => (p >= 100 ? 100 : Math.min(100, p + step))), 50)
    return () => clearInterval(id)
  }, [durationMs])

  // At 100%: make sure every checkmark is shown (the last bar fills as the ramp
  // stops, so don't rely on its transition alone), hold, then complete.
  useEffect(() => {
    if (percent < 100) return
    const checks = setTimeout(() => setDoneBars(Array.from({ length: n }, (_, i) => i)), 400)
    const complete = setTimeout(() => onCompleteRef.current(), holdMs)
    return () => {
      clearTimeout(checks)
      clearTimeout(complete)
    }
  }, [percent, n, holdMs])

  return (
    <div className="relative flex flex-1 flex-col px-4">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px]"
        style={{
          background:
            "radial-gradient(120% 90% at 50% -10%, rgba(100,30,220,0.2), transparent 62%)",
        }}
      />

      <div className="relative z-10 flex flex-1 flex-col items-center pt-8 text-center">
        <div className="relative size-[220px]">
          <img src={orb} alt="" className="absolute inset-0 size-full object-contain" />
          {ring && <img src={ring} alt="" className="absolute inset-[3%]" />}
          <span className="absolute inset-0 flex items-center justify-center font-display text-[40px] font-bold text-white">
            {Math.round(percent)}%
          </span>
        </div>

        <h1 className="mt-6 font-display text-2xl font-medium text-purple-light">{title}</h1>

        <div className="mt-8 w-full space-y-4 text-left">
          {checklist.map((item, i) => {
            const fill = Math.max(0, Math.min(1, (percent - (i * 100) / n) / (100 / n)))
            return (
              <div key={item} className="space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-base text-purple-light/70">{item}</p>
                  {doneBars.includes(i) && <Check className="size-5 shrink-0 text-green" />}
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-purple-light/10">
                  <div
                    className="h-full rounded-full bg-main-purple transition-[width] duration-300 ease-out"
                    style={{ width: `${fill * 100}%` }}
                    onTransitionEnd={(e) => {
                      if (e.propertyName === "width" && fill >= 1) {
                        setDoneBars((d) => (d.includes(i) ? d : [...d, i]))
                      }
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
